import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Bot, Send, Copy, Check, Sparkles, Mail, Bell, FileText, MessageSquare, User, Pencil, SendHorizonal, Reply, Package, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAIAssistantPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [clientEmail, setClientEmail] = useState("");
  const [clientName, setClientName] = useState("");
  const [actionType, setActionType] = useState("follow_up");
  const [customPrompt, setCustomPrompt] = useState("");
  const [tone, setTone] = useState("professional");
  
  // Results
  const [generatedText, setGeneratedText] = useState("");
  const [editedText, setEditedText] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [clientInfo, setClientInfo] = useState(null);
  
  // Client list for quick selection
  const [clients, setClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Order Status fields
  const [orderNumber, setOrderNumber] = useState("");
  const [garmentType, setGarmentType] = useState("");
  const [orderStatus, setOrderStatus] = useState("pending");
  
  // Garment type options
  const garmentTypes = ["Coat", "2pcs Suit", "3pcs Suit", "Shirt", "Pants", "Vest", "Tuxedo", "Overcoat"];

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchClients();
      
      // Check if action type was pre-selected from dashboard
      const preSelectedAction = sessionStorage.getItem("aiActionType");
      if (preSelectedAction) {
        setActionType(preSelectedAction);
        sessionStorage.removeItem("aiActionType");
      }
    }
  }, [navigate]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      // Get unique clients by email
      const uniqueClients = {};
      response.data.forEach(apt => {
        if (apt.client_email && !uniqueClients[apt.client_email]) {
          uniqueClients[apt.client_email] = {
            name: apt.client_name,
            email: apt.client_email,
            phone: apt.client_phone
          };
        }
      });
      setClients(Object.values(uniqueClients));
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const handleGenerate = async () => {
    if (!clientEmail && !clientName && actionType !== "custom" && actionType !== "respond_to_client" && actionType !== "order_status") {
      toast.error("Please enter a client email or name");
      return;
    }
    
    if (actionType === "custom" && !customPrompt) {
      toast.error("Please enter a custom prompt");
      return;
    }

    if (actionType === "respond_to_client" && !customPrompt) {
      toast.error("Please paste the client's message to respond to");
      return;
    }

    if (actionType === "order_status") {
      if (!clientName) {
        toast.error("Please enter the client name");
        return;
      }
      if (!orderNumber) {
        toast.error("Please enter the order number");
        return;
      }
      if (!garmentType) {
        toast.error("Please select a garment type");
        return;
      }
    }

    setLoading(true);
    setGeneratedText("");
    setEditedText("");
    setEmailSubject("");
    setClientInfo(null);
    setIsEditing(false);

    try {
      const response = await axios.post(`${API}/admin/ai-assistant`, {
        client_email: clientEmail || null,
        client_name: clientName || null,
        action_type: actionType,
        custom_prompt: customPrompt,
        tone: tone,
        order_number: orderNumber || null,
        garment_type: garmentType || null,
        order_status: orderStatus || null
      });

      const text = response.data.generated_text;
      setGeneratedText(text);
      setEditedText(text);
      
      // Extract subject from generated text if present
      const subjectMatch = text.match(/Subject:\s*(.+?)(?:\n|$)/i);
      if (subjectMatch) {
        setEmailSubject(subjectMatch[1].trim());
      } else {
        // Default subjects based on action type
        const defaultSubjects = {
          "follow_up": "Following Up - Laguerre Custom Tailoring",
          "respond_to_client": "Re: Your Inquiry - Laguerre Custom Tailoring",
          "order_status": orderStatus === "complete" 
            ? `Your Order ${orderNumber} is Ready! - Laguerre Custom Tailoring`
            : `Order Update: ${orderNumber} - Laguerre Custom Tailoring`,
          "appointment_reminder": "Appointment Reminder - Laguerre Custom Tailoring",
          "measurement_reminder": "Measurement Submission Reminder - Laguerre",
          "custom": "Message from Laguerre Custom Tailoring"
        };
        setEmailSubject(defaultSubjects[actionType] || "Message from Laguerre");
      }
      
      setClientInfo(response.data.client_info);
      toast.success("Content generated successfully!");
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate content");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editedText || generatedText);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendEmail = async () => {
    if (!clientEmail) {
      toast.error("Please enter a client email address");
      return;
    }
    
    if (!editedText && !generatedText) {
      toast.error("Please generate content first");
      return;
    }

    setSending(true);
    
    try {
      // Remove subject line from body if present
      let emailBody = editedText || generatedText;
      emailBody = emailBody.replace(/Subject:\s*.+?(?:\n|$)/i, '').trim();
      
      await axios.post(`${API}/admin/send-email`, {
        recipient_email: clientEmail,
        recipient_name: clientName || clientInfo?.name || "",
        subject: emailSubject,
        body: emailBody
      });
      
      toast.success(`Email sent to ${clientEmail}!`);
      setIsEditing(false);
    } catch (error) {
      console.error("Send email error:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const selectClient = (client) => {
    setClientEmail(client.email);
    setClientName(client.name);
    setShowClientDropdown(false);
  };

  const actionTypes = [
    { id: "follow_up", label: "Follow-up Email", icon: Mail, description: "Check on satisfaction & invite to book again" },
    { id: "respond_to_client", label: "Respond to Client", icon: Reply, description: "Generate a reply to a client's message" },
    { id: "order_status", label: "Order Status Update", icon: Package, description: "Generate order pending/complete message" },
    { id: "appointment_reminder", label: "Appointment Reminder", icon: Bell, description: "Remind about upcoming appointment" },
    { id: "measurement_reminder", label: "Measurement Reminder", icon: FileText, description: "Remind to submit measurements" },
    { id: "custom", label: "Custom Message", icon: MessageSquare, description: "Write your own prompt" }
  ];

  const tones = [
    { id: "professional", label: "Professional" },
    { id: "friendly", label: "Friendly" },
    { id: "formal", label: "Formal" }
  ];

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">
                  AI Assistant
                </h1>
                <p className="text-sm text-muted-foreground">
                  Generate client communications powered by AI
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Client Selection */}
              <div className="bg-card border border-white/10 p-6">
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <User size={18} className="text-primary" />
                  Select Client
                </h3>
                
                <div className="space-y-4">
                  <div className="relative">
                    <Input
                      placeholder="Client email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      onFocus={() => setShowClientDropdown(true)}
                      onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                      className="bg-background border-white/10"
                    />
                    
                    {/* Client Dropdown */}
                    {showClientDropdown && clients.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-white/10 max-h-48 overflow-y-auto z-10">
                        {clients
                          .filter(c => 
                            c.email.toLowerCase().includes(clientEmail.toLowerCase()) ||
                            c.name.toLowerCase().includes(clientEmail.toLowerCase())
                          )
                          .slice(0, 5)
                          .map((client, idx) => (
                            <button
                              key={idx}
                              onClick={() => selectClient(client)}
                              className="w-full px-4 py-2 text-left hover:bg-white/5 transition-colors"
                            >
                              <div className="text-sm font-medium">{client.name}</div>
                              <div className="text-xs text-muted-foreground">{client.email}</div>
                            </button>
                          ))
                        }
                      </div>
                    )}
                  </div>
                  
                  <div className="text-center text-xs text-muted-foreground">or</div>
                  
                  <Input
                    placeholder="Client name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="bg-background border-white/10"
                  />
                </div>
                
                {/* Show selected client info */}
                {clientInfo && (
                  <div className="mt-4 p-3 bg-primary/5 border border-primary/20 text-sm">
                    <div className="text-primary font-medium mb-1">Client Found</div>
                    <div>{clientInfo.name}</div>
                    <div className="text-muted-foreground">{clientInfo.email}</div>
                    {clientInfo.phone && (
                      <div className="text-muted-foreground">{clientInfo.phone}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Type Selection */}
              <div className="bg-card border border-white/10 p-6">
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <Sparkles size={18} className="text-primary" />
                  Message Type
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {actionTypes.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => setActionType(action.id)}
                      className={`p-4 text-left transition-colors border ${
                        actionType === action.id
                          ? "border-primary bg-primary/5"
                          : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <action.icon size={16} className={actionType === action.id ? "text-primary" : "text-muted-foreground"} />
                        <span className="text-sm font-medium">{action.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{action.description}</p>
                    </button>
                  ))}
                </div>
                
                {/* Respond to Client - paste client message */}
                {actionType === "respond_to_client" && (
                  <div className="mt-4">
                    <label className="block text-sm text-muted-foreground mb-2">
                      Paste the client's message below:
                    </label>
                    <Textarea
                      placeholder="Paste the client's email or message here... (e.g., 'Hi, I wanted to ask about the turnaround time for a custom suit...')"
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      className="bg-background border-white/10 min-h-[120px]"
                    />
                  </div>
                )}

                {/* Order Status - order details input */}
                {actionType === "order_status" && (
                  <div className="mt-4 space-y-4">
                    <div className="p-4 bg-primary/5 border border-primary/20">
                      <div className="flex items-center gap-2 mb-3">
                        <ClipboardList size={18} className="text-primary" />
                        <span className="text-sm font-medium">Order Details</span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Order Number</label>
                          <Input
                            placeholder="e.g., DDLR25100015"
                            value={orderNumber}
                            onChange={(e) => setOrderNumber(e.target.value)}
                            className="bg-background border-white/10"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">Garment Type</label>
                          <Select value={garmentType} onValueChange={setGarmentType}>
                            <SelectTrigger className="bg-background border-white/10">
                              <SelectValue placeholder="Select garment" />
                            </SelectTrigger>
                            <SelectContent>
                              {garmentTypes.map((type) => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <label className="block text-xs text-muted-foreground mb-2">Order Status</label>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setOrderStatus("pending")}
                            className={`flex-1 px-4 py-3 text-sm transition-colors border ${
                              orderStatus === "pending"
                                ? "border-amber-500 bg-amber-500/10 text-amber-400"
                                : "border-white/10 hover:border-white/20"
                            }`}
                          >
                            <Package size={16} className="mx-auto mb-1" />
                            Order Pending
                          </button>
                          <button
                            onClick={() => setOrderStatus("complete")}
                            className={`flex-1 px-4 py-3 text-sm transition-colors border ${
                              orderStatus === "complete"
                                ? "border-green-500 bg-green-500/10 text-green-400"
                                : "border-white/10 hover:border-white/20"
                            }`}
                          >
                            <Check size={16} className="mx-auto mb-1" />
                            Order Complete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom prompt input */}
                {actionType === "custom" && (
                  <Textarea
                    placeholder="Enter your custom prompt... (e.g., 'Write a thank you note for their recent order')"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    className="mt-4 bg-background border-white/10 min-h-[100px]"
                  />
                )}
              </div>

              {/* Tone Selection */}
              <div className="bg-card border border-white/10 p-6">
                <h3 className="font-serif text-lg mb-4">Tone</h3>
                <div className="flex gap-3">
                  {tones.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTone(t.id)}
                      className={`px-4 py-2 text-sm transition-colors border ${
                        tone === t.id
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-foreground py-6"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    Generate Content
                  </>
                )}
              </Button>
            </motion.div>

            {/* Output Section */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:sticky lg:top-24"
            >
              <div className="bg-card border border-white/10 p-6 min-h-[500px] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-lg">Generated Content</h3>
                  {generatedText && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(!isEditing)}
                        className="border-white/20"
                      >
                        <Pencil size={14} className="mr-1" />
                        {isEditing ? "Preview" : "Edit"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopy}
                        className="border-white/20"
                      >
                        {copied ? (
                          <>
                            <Check size={14} className="mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy size={14} className="mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Email Subject (shown when content is generated) */}
                {generatedText && (
                  <div className="mb-4">
                    <label className="block text-xs text-muted-foreground mb-1">Email Subject</label>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Email subject line..."
                      className="bg-background border-white/10"
                    />
                  </div>
                )}
                
                <div className="flex-1 bg-background border border-white/5 p-4 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">Generating with AI...</p>
                      </div>
                    </div>
                  ) : generatedText ? (
                    isEditing ? (
                      <Textarea
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                        className="w-full h-full min-h-[300px] bg-transparent border-none resize-none text-sm leading-relaxed focus:ring-0"
                        placeholder="Edit your email content..."
                      />
                    ) : (
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">
                        {editedText || generatedText}
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                      <div className="text-center">
                        <Bot size={32} className="mx-auto mb-3 opacity-50" />
                        <p>Generated content will appear here</p>
                        <p className="text-xs mt-1">Select a client and message type to get started</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Send Email Button */}
                {generatedText && (
                  <Button
                    onClick={handleSendEmail}
                    disabled={sending || !clientEmail}
                    className="mt-4 w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    {sending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <SendHorizonal size={18} className="mr-2" />
                        Send Email to {clientEmail || "Client"}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminAIAssistantPage;
